import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './DataObjectVersionFilters.translations.json';
import { Dayjs } from 'dayjs';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { keepPreviousData } from '@tanstack/react-query';
import styled from 'styled-components';
import {
  valueDate as getValueDate,
  DataObjectGroupType,
  DataObjectOutputType,
  parseGroupDescriptionRef,
  updateFrequency,
  ValidPeriodOptions,
  isDisabledDate,
  getLatestOutputVersionId,
  DataObjectVersionType,
} from '../dataobject';
import { useDataObjectGroupsWithDescriptionsQuery } from '../dataObjectGroup';
import { getDayjs, ISO_DATE_FORMAT } from '../common/utils';
import { FormField } from '../common/form';
import { FormPeriodEndPicker } from '../common/form/input/FormPeriodEndPicker';
import { FormDataObjectVersionSelect } from '../common/form/input/FormDataObjectVersionSelect';

const { addTranslations, t } = i18nNsFactory(
  'visual-validation-data-object-filters',
);
addTranslations(translations);

type Props = {
  versions: DataObjectVersionType[];
  isLoading?: boolean;
  outputType?: DataObjectOutputType;
  valueDateFieldName?: string;
  versionFieldName?: string;
  periodOptions: ValidPeriodOptions;
  setPeriodOptions: Dispatch<SetStateAction<ValidPeriodOptions>>;
};

export const DataObjectVersionFilters = ({
  versions,
  isLoading,
  outputType,
  valueDateFieldName = 'valueDate',
  versionFieldName = 'version',
  periodOptions,
  setPeriodOptions,
}: Props) => {
  const { setValue } = useFormContext();
  const [valueDate, groupDescriptionRef, selectedVersion] = useWatch({
    name: [valueDateFieldName, 'groupDescriptionRef', versionFieldName],
  });

  const { descriptionId, groupId } = useMemo(
    () =>
      groupDescriptionRef
        ? parseGroupDescriptionRef(groupDescriptionRef)
        : { groupId: undefined, descriptionId: undefined },
    [groupDescriptionRef],
  );

  const { data: dataObjectGroupsWithDescriptions } =
    useDataObjectGroupsWithDescriptionsQuery(
      {
        groupType: DataObjectGroupType.O,
        outputType,
      },
      {
        placeholderData: keepPreviousData,
      },
    );

  useEffect(() => {
    if (descriptionId) {
      updateFrequency(
        'description',
        descriptionId,
        setPeriodOptions,
        dataObjectGroupsWithDescriptions,
        groupId,
      );
    }
  }, [
    descriptionId,
    groupId,
    dataObjectGroupsWithDescriptions,
    setValue,
    setPeriodOptions,
  ]);

  useEffect(() => {
    if (periodOptions.frequency && !valueDate) {
      const closestValueDate = getValueDate(
        null,
        periodOptions.frequency,
        periodOptions.validRanges,
      )?.format(ISO_DATE_FORMAT);
      setValue(
        valueDateFieldName,
        closestValueDate ? closestValueDate : undefined,
      );
    }
  }, [periodOptions, setValue, valueDate, valueDateFieldName]);

  useEffect(() => {
    const valueDateDayJs = getDayjs(valueDate);
    if (!valueDateDayJs) {
      return;
    }

    // If there are no versions for the selected output, clear the version selection when not loading
    if (!versions || versions.length === 0) {
      if (!isLoading && groupDescriptionRef) {
        setValue(versionFieldName, null);
      }
      return;
    }

    const selectedVersionNumber =
      typeof selectedVersion === 'number'
        ? selectedVersion
        : Number(selectedVersion);
    const hasSelectedVersion = versions.some(
      (version) => version.id === selectedVersionNumber,
    );
    // If the currently selected version is valid for the new value date, keep it selected
    if (hasSelectedVersion) {
      return;
    }

    const latestVersionId = getLatestOutputVersionId(
      versions,
      valueDateDayJs.format('YYYY-MM-DD'),
    );
    // If not, select the latest valid version for the new value date select it
    if (latestVersionId) {
      setValue(versionFieldName, latestVersionId);
      return;
    }

    // If there are no valid versions for the new value date, clear the version selection
    setValue(versionFieldName, null);
  }, [
    versions,
    valueDate,
    selectedVersion,
    isLoading,
    groupDescriptionRef,
    setValue,
    versionFieldName,
  ]);

  useEffect(() => {
    if (!descriptionId) {
      setValue(valueDateFieldName, undefined);
      setValue(versionFieldName, null);
      setPeriodOptions({});
    }
  }, [
    descriptionId,
    setValue,
    valueDateFieldName,
    versionFieldName,
    setPeriodOptions,
  ]);

  return (
    <>
      <StyledFormField
        name={valueDateFieldName}
        label={t(`fields.${valueDateFieldName}.label`)}
        required
      >
        <FormPeriodEndPicker
          name={valueDateFieldName}
          frequency={periodOptions?.frequency ?? 'WEEK'}
          disabledDate={(date: Dayjs) => {
            const correctedDate =
              periodOptions?.frequency === 'MONTH'
                ? date.endOf('month').startOf('day')
                : date;
            return isDisabledDate(correctedDate, periodOptions.validRanges);
          }}
          onChange={() => {
            setValue(versionFieldName, undefined);
          }}
          disabled={!groupDescriptionRef}
        />
      </StyledFormField>
      <StyledFormField
        name={versionFieldName}
        label={t(`fields.${versionFieldName}.label`)}
        required
      >
        <FormDataObjectVersionSelect
          name={versionFieldName}
          versions={versions || []}
          isLoading={isLoading}
          disabled={!versions || versions.length === 0 || !valueDate}
          renderCalculationTimeInLabel={false}
          placeholder={t(`fields.${versionFieldName}.placeholder`)}
        />
      </StyledFormField>
    </>
  );
};

const StyledFormField = styled(FormField)`
  width: 216px;
`;
